import { ScoreSet } from '@/modules/score-set/types';
import { EventType, NotificationTypes } from '@/common/types';
import { ScoreSetEvent } from '@/modules/epoch/types';
import { isSleepOverlapRequiredEvent } from '@/core/helpers';
import { i18n } from '../i18n-instance';

const { t } = i18n.global;

type ValidationMessage = {
  key: string;
  type: NotificationTypes;
  optional?: any;
  params?: Record<string, string>;
};

// TODO: This method should return validation status with keys.
export const isEventValid = (
  type: string,
  startDate: string,
  duration: number,
  scoreSet: ScoreSet,
  events: ScoreSetEvent[]
): ValidationMessage | undefined => {
  const startTime = new Date(startDate).getTime();
  const endTime = startTime + duration * 1000;

  const [lightsOff, lightsOn] = [
    new Date(scoreSet.lightsOff).getTime(),
    new Date(scoreSet.lightsOn).getTime()
  ];
  if (lightsOff > startTime || lightsOn < endTime) {
    return { key: 'lightsLimit', type: NotificationTypes.Error };
  }

  if (type === EventType.AROUSAL_FRONTAL_L || type === EventType.AROUSAL_FRONTAL_R) {
    if (duration < 3) {
      return { key: 'arousalLimit', type: NotificationTypes.Error };
    }
  }

  if (type === EventType.BRUXISM_PHASIC_L || type === EventType.BRUXISM_PHASIC_R) {
    if (duration < 0.25) {
      return {
        key: 'minimumEventDurationLimit',
        params: { eventType: t(type), duration: '0.25' },
        type: NotificationTypes.Error
      };
    }
  }

  if (type === EventType.BRUXISM_TONIC_L || type === EventType.BRUXISM_TONIC_R) {
    if (duration < 2) {
      return {
        key: 'minimumEventDurationLimit',
        params: { eventType: t(type), duration: '2' },
        type: NotificationTypes.Error
      };
    }
  }

  if (isSleepOverlapRequiredEvent(type as EventType)) {
    if (hasOnlyWakeOrUnstagedOverlap(startTime, duration, events)) {
      return { key: 'inWakeStageWarning', type: NotificationTypes.Warning };
    }
  }

  if ([EventType.TACHYCARDIA, EventType.BRADYCARDIA].includes(type as EventType)) {
    if (duration < 30) {
      return { key: 'cardiacEventLimit', type: NotificationTypes.Error };
    }
  }

  if (
    [
      EventType.ASYSTOLE,
      EventType.NARROW_COMPLEX_TACHYCARDIA,
      EventType.WIDE_COMPLEX_TACHYCARDIA
    ].includes(type as EventType)
  ) {
    if (duration < 3) {
      return { key: 'arrhythmiaLimit', type: NotificationTypes.Error };
    }
  }

  if ([EventType.LEG_MOVEMENT, EventType.PERIODIC_LEG_MOVEMENT].includes(type as EventType)) {
    if (duration < 0.5) {
      return { key: 'legMovementLimit', type: NotificationTypes.Error };
    }

    if (duration >= 10) {
      return { key: 'legMovementLimit', type: NotificationTypes.Error };
    }
  }
};

export const checkValidationWithNotification = (
  type: string,
  startDate: string,
  duration: number,
  scoreSet: ScoreSet,
  events: ScoreSetEvent[],
  store: any
) => {
  const validationMessage = isEventValid(type, startDate, duration, scoreSet, events);

  if (validationMessage) {
    store.commit(
      'addNotification',
      {
        i18nTitle: 'errorAddScoreSetEvent',
        i18nDescription: validationMessage.key,
        i18nParams: validationMessage.params,
        type: validationMessage.type
      },
      { root: true }
    );
    return false;
  }

  return true;
};

/**
 * Don't allow create or update events only in wake stages or in unstaged
 * This method helps to check wake stage bu start time and duration
 * @param newEventStartTime
 * @param newEventDuration
 * @param events
 * @returns Boolean
 */
const hasOnlyWakeOrUnstagedOverlap = (
  newEventStartTime: number,
  newEventDuration: number,
  events: ScoreSetEvent[]
): boolean => {
  const sleepEvents = events['Sleep_Stages'] || [];
  const eventEnd = newEventStartTime + newEventDuration * 1000;

  let hasSleepOverlap = false;
  let hasWakeOrUnstagedOverlap = false;

  for (const sleepStage of sleepEvents) {
    const sleepStageStart = new Date(sleepStage.start).getTime();
    const sleepStageEnd = sleepStageStart + sleepStage.duration * 1000;

    const overlapStart = Math.max(sleepStageStart, newEventStartTime);
    const overlapEnd = Math.min(sleepStageEnd, eventEnd);
    const overlapDuration = overlapEnd - overlapStart;

    if (overlapDuration > 0) {
      if (sleepStage.eventType === EventType.WAKE || sleepStage.eventType === EventType.UNSTAGED) {
        hasWakeOrUnstagedOverlap = true;
      } else {
        hasSleepOverlap = true;
      }
    }
  }

  if (hasWakeOrUnstagedOverlap && hasSleepOverlap) {
    return false; // it is ok to have wake or unstaged overlap if it has also a sleep overlap
  }

  return hasWakeOrUnstagedOverlap;
};
